import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { fileteDasAbas } from '@/componentes/filete';
import { PaginaSemPermissao } from '@/componentes/SemPermissao';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDoOrcamento } from './api';
import { CaixaDoOrcamento } from './CaixaDoOrcamento';
import { CenariosDoOrcamento } from './CenariosDoOrcamento';
import { OrcamentoPorCentro } from './OrcamentoPorCentro';
import { validarBuscaDoOrcamento, type AbaDoOrcamento } from './orcamento';
import { PainelDoDesvio, PrevistoRealizado } from './PrevistoRealizado';

const rota = getRouteApi('/orcamento');

const ABAS: readonly (readonly [AbaDoOrcamento, string])[] = [
  ['orcamento', 'Orçamento por centro'],
  ['desvios', 'Previsto × realizado'],
  ['cenarios', 'Cenários'],
  ['caixa', 'Caixa'],
];

/** A ação principal da aba, que mora no cabeçalho (um primário por tela). */
export type AcaoDoCabecalho = 'linha' | 'versao' | 'cenario' | 'saldo' | null;

/**
 * MÓDULO 19 — ORÇAMENTO E PLANEJAMENTO (0106).
 *
 * Quatro abas, uma ferramenta do v6 em cada: o orçamento por centro (a grade
 * de meses do rascunho, com a barra de salvar flutuante, as premissas com
 * fonte e a aprovação por quem não montou), o previsto × comprometido ×
 * realizado (cada valor leva aos registros que o formam, e o desvio relevante
 * pede explicação), os cenários (cenário não é previsão, e a frase vai junto)
 * e a necessidade de caixa (o menor saldo e a data, com o saldo do banco
 * informado à mão).
 *
 * Sem desenho no manual (o mapa dos módulos, p.50 a p.60, não tem o 19): segue
 * o hub com abas (p.40), a faixa de indicadores (p.26), a tabela com painel de
 * detalhe (p.38), o formulário em gaveta (p.31) e a aprovação com a alçada à
 * vista (p.44). Dúvida de design registrada no relatório.
 */
export function PaginaOrcamento() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('orcamento.ler');
  const grava = permissoes.includes('orcamento.gravar');
  const informaSaldo = permissoes.includes('caixa.informar_saldo');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const aba: AbaDoOrcamento = busca.aba ?? 'orcamento';
  const [acao, setAcao] = useState<AcaoDoCabecalho>(null);
  /* Com a grade alterada, o primário da tela é o "Salvar" da barra flutuante:
     o "Adicionar linha" do cabeçalho vira secundário. */
  const [comAlteracoes, setComAlteracoes] = useState(false);
  /* A linha aberta do previsto x realizado: o painel mora AO LADO da lista
     (p.29), fora do conteiner que rola, e por isso o estado sobe para cá. */
  /* 0109: o aviso do desvio sem explicação abre a linha dele (`?desvio=centro|conta`). */
  const [desvioAberto, setDesvioAberto] = useState<string | null>(busca.desvio ?? null);

  const versoes = useQuery({ queryKey: ['orcamento', 'versoes'], queryFn: apiDoOrcamento.versoes, enabled: pode, meta: { oQueLia: 'as versões do orçamento' } });
  const versaoId = busca.versao ?? versoes.data?.atual ?? null;
  const versao = versoes.data?.versoes.find((v) => v.id === versaoId) ?? null;

  if (sessao.data && !pode) {
    return <PaginaSemPermissao sobretitulo="Orçamento e planejamento" titulo="Orçamento e caixa" modulo="o orçamento e o caixa" licenca="orcamento.ler" />;
  }

  const primario = (() => {
    if (aba === 'orcamento' && grava && versoes.data && !versao) return { acao: 'versao' as const, rotulo: 'Criar versão' };
    if (aba === 'orcamento' && grava && versao?.situacao === 'rascunho') return { acao: 'linha' as const, rotulo: 'Adicionar linha' };
    if (aba === 'cenarios' && grava && versao) return { acao: 'cenario' as const, rotulo: 'Criar cenário' };
    if (aba === 'caixa' && informaSaldo) return { acao: 'saldo' as const, rotulo: 'Informar saldo' };
    return null;
  })();

  return (
    <>
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Orçamento e planejamento"
          titulo="Orçamento e caixa"
          subtitulo="O plano por centro e conta, o previsto contra o realizado, os cenários e o caixa que a operação vai pedir"
          acoes={
            primario ? (
              <Botao
                nivel={primario.acao === 'linha' && comAlteracoes ? 'secundario' : 'primario'}
                onClick={() => {
                  setAcao(primario.acao);
                }}
              >
                {primario.rotulo}
              </Botao>
            ) : null
          }
        />

        <div role="tablist" aria-label="Seções do orçamento" ref={fileteDasAbas} className="barra-de-abas filete-das-abas">
          {ABAS.map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={aba === id}
              onClick={() => {
                setAcao(null);
                setDesvioAberto(null);
                void navegar({ search: validarBuscaDoOrcamento({ ...busca, aba: id === 'orcamento' ? undefined : id }) });
              }}
              className="aba"
            >
              {rotulo}
            </button>
          ))}
        </div>

        {aba === 'orcamento' ? (
          <OrcamentoPorCentro
            versoes={versoes.data ?? null}
            carregandoVersoes={versoes.isPending}
            erroDasVersoes={versoes.isError ? versoes.error : null}
            aoTentarDeNovo={() => void versoes.refetch()}
            versaoId={versaoId}
            aoEscolherVersao={(v) => {
              void navegar({ search: validarBuscaDoOrcamento({ ...busca, versao: v }) });
            }}
            acao={acao}
            aoFecharAcao={() => {
              setAcao(null);
            }}
            aoMudarAlteracoes={setComAlteracoes}
          />
        ) : null}
        {aba === 'desvios' ? (
          <PrevistoRealizado
            versaoId={versao?.situacao === 'aprovado' ? versao.id : null}
            competencia={busca.competencia ?? null}
            aoEscolherCompetencia={(c) => {
              setDesvioAberto(null);
              void navegar({ search: validarBuscaDoOrcamento({ ...busca, competencia: c.slice(0, 7) }) });
            }}
            aberta={desvioAberto}
            aoAbrir={setDesvioAberto}
          />
        ) : null}
        {aba === 'cenarios' ? (
          <CenariosDoOrcamento
            versao={versao}
            carregandoVersoes={versoes.isPending}
            acao={acao}
            aoFecharAcao={() => {
              setAcao(null);
            }}
          />
        ) : null}
        {aba === 'caixa' ? (
          <CaixaDoOrcamento
            acao={acao}
            aoFecharAcao={() => {
              setAcao(null);
            }}
          />
        ) : null}
      </div>
    </div>
      {aba === 'desvios' && desvioAberto ? (
        <PainelDoDesvio
          key={desvioAberto}
          chave={desvioAberto}
          competencia={busca.competencia ?? null}
          versaoId={versao?.situacao === 'aprovado' ? versao.id : null}
          aoFechar={() => {
            setDesvioAberto(null);
          }}
        />
      ) : null}
    </>
  );
}
